<template>
  <v-container class="pa-2 page-layout" fluid>
    <v-row dense>
      <v-col class="py-0">
        <h1 class="page-title">Provider Search</h1>
      </v-col>
    </v-row>

    <v-row dense class="provider-filters-row">
      <v-col cols="12" sm="6" md="2" lg="3" class="pb-0">
        <v-text-field
          v-model="searchTerm"
          id="providerSearch"
          label="Search (name or NPI)"
          placeholder="Name or NPI..."
          autofocus
          clearable
          outlined
          dense
          prepend-inner-icon="mdi-magnify"
          @click:clear="clearSearch"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="2" lg="2" class="pb-0">
        <v-select
          v-model="providerTypeFilter"
          :items="providerTypeOptions"
          label="Primary Care or Specialist"
          outlined
          dense
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="2" lg="2" class="pb-0">
        <v-text-field
          v-model="organizationFilter"
          label="Organization"
          placeholder="Organization..."
          clearable
          outlined
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="2" lg="2" class="pb-0">
        <v-combobox
          v-model="specialtyFilter"
          :items="specialtyOptions"
          label="Specialty"
          placeholder="Search, select, or type"
          clearable
          outlined
          dense
          hide-details
        ></v-combobox>
      </v-col>
      <v-col cols="12" sm="6" md="2" lg="2" class="pb-0">
        <v-text-field
          v-model="zipCodeFilter"
          label="Zip / location"
          placeholder="ZIP or City, State (radius search)"
          clearable
          outlined
          dense
        ></v-text-field>
      </v-col>
      <v-col v-if="isAffiliation" cols="12" sm="6" md="2" lg="2" class="pb-0 d-flex align-center">
        <v-checkbox
          v-model="affiliationOnlyFilter"
          label="My affiliated providers only"
          hide-details
          dense
          @change="onAffiliationFilterChange"
        ></v-checkbox>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-card flat class="rounded-lg">
          <v-data-table
            :loading="loading"
            :headers="tableHeaders"
            :items="providersWithDetails"
            item-key="ROW_KEY"
            :server-items-length="totalProviders"
            :page="pagination.page"
            :items-per-page="pagination.limit"
            :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
            :no-data-text="loading ? 'Loading...' : 'No matching providers...'"
            class="providers-table"
            dense
            @update:page="onPageChange"
            @update:items-per-page="onItemsPerPageChange"
          >
            <template v-slot:item.STATUS="{ item }">
              <v-tooltip left v-if="!hideProviderStatus">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" small :color="getProviderStatus(item)">mdi-circle</v-icon>
                </template>
                <span>{{ getProviderStatusTooltip(item) }}</span>
              </v-tooltip>
              <span v-else>—</span>
            </template>
            <template v-slot:item.PROVIDER_NAME="{ item }">
              <span class="font-weight-medium">{{ item.PROVIDER_NAME || '—' }}</span>
            </template>
            <template v-slot:item.PROVIDER_ID="{ item }">
              <v-chip small outlined color="secondary">{{ item.PROVIDER_ID || '—' }}</v-chip>
            </template>
            <template v-slot:item.PRIMARY_SPECIALTY="{ item }">
              {{ item.PRIMARY_SPECIALTY || '—' }}
            </template>
            <template v-slot:item.ORGANIZATION="{ item }">
              {{ organizationName(item) || '—' }}
            </template>
            <template v-slot:item.ADDRESS="{ item }">
              {{ fullAddress(item) || '—' }}
            </template>
            <template v-slot:item.PHONE="{ item }">
              {{ phoneNumber(item) || '—' }}
            </template>
            <template v-slot:item.DETAILS="{ item }">
              <provider-modal
                v-if="!hideProviderStatus || useLegacyProviderDetails"
                :provider="item"
                :insurance="item.PLAN"
                :group="(user && user.group) || []"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" small color="primary" text>
                    <v-icon small left>mdi-information-outline</v-icon>
                    View details
                  </v-btn>
                </template>
              </provider-modal>
              <v-btn
                v-else
                small
                color="primary"
                text
                @click.stop="openProviderDetails(item)"
              >
                <v-icon small left>mdi-information-outline</v-icon>
                View details
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog FHC: controlado diretamente pelo v-model -->
    <v-dialog v-model="detailsDialogOpen" max-width="700" persistent transition="dialog-transition" @input="onDetailsDialogChange">
      <ProviderDetailsFHC
        v-if="selectedProvider"
        :key="providerDetailsDialogKey"
        :provider="selectedProvider"
        :hide-rates="hideRatesForSelected"
        :hide-capitation-pricing-and-other-info="true"
        content-only
        @close="closeDetailsDialog"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';
import ProviderModal from '../components/ProviderModal.vue';
import ProviderDetailsFHC from '../components/ProviderDetailsFHC.vue';

export default {
  name: 'providers',
  data() {
    return {
      user: '',
      providers: [],
      groups: [],
      insurances: [],
      selectedInsurance: 'all',
      errors: [],
      searchTerm: '',
      providerTypeFilter: 'all',
      providerTypeOptions: [
        { text: 'All', value: 'all' },
        { text: 'Primary Care', value: 'primarycare' },
        { text: 'Specialist', value: 'specialist' },
        { text: 'Dual specialty', value: 'dual' },
      ],
      organizationFilter: '',
      specialtyFilter: '',
      specialtyOptions: [],
      zipCodeFilter: '',
      zipSearchRadiusMiles: 15,
      affiliationOnlyFilter: false,
      loading: true,
      selectedProvider: null,
      detailsDialogOpen: false,
      totalProviders: 0,
      pagination: { page: 1, limit: 15 },
    };
  },
  components: {
    ProviderModal,
    ProviderDetailsFHC,
  },
  computed: {
    isAffiliation() {
      return this.user && (this.user.role || '').toLowerCase() === 'affiliation';
    },
    hideRatesForSelected() {
      if (!this.user || !this.selectedProvider) return false;
      const role = (this.user.role || '').toLowerCase();
      if (role === 'provider' || role === 'specialist') return true;
      if (role === 'affiliation') {
        const userNpi = (this.user.npi || this.user.NPI || '').toString().trim();
        if (!userNpi) return true;
        const aff = this.selectedProvider.IPA_AFFILIATION;
        if (aff == null || aff === '') return true;
        const affStr = Array.isArray(aff) ? aff.join(',') : String(aff);
        return affStr.toLowerCase().indexOf(userNpi.toLowerCase()) === -1;
      }
      return false;
    },
    hideProviderStatus() {
      const customer = (process.env.VUE_APP_CUSTOMER || '').toLowerCase();
      if (customer === 'familyhealthchoice' || customer === 'floridahealthchoice') return true;
      if (customer.indexOf('family') >= 0 && customer.indexOf('health') >= 0) return true;
      if (this.user && this.user.insurance && Array.isArray(this.user.insurance)) {
        return this.user.insurance.some(
          (ins) => ins === 'FamilyHealthChoice' || ins === 'FloridaHealthChoice'
        );
      }
      return false;
    },
    /** Modal fullscreen antigo (ProviderModal com abas) no Provider Search para FHC — ver VUE_APP_PROVIDER_SEARCH_LEGACY_DETAILS. */
    useLegacyProviderDetails() {
      const v = String(process.env.VUE_APP_PROVIDER_SEARCH_LEGACY_DETAILS || '')
        .toLowerCase()
        .trim();
      return v === 'true' || v === '1' || v === 'yes';
    },
    tableHeaders() {
      const h = [];
      if (!this.hideProviderStatus) {
        h.push({ text: '', value: 'STATUS', sortable: false, width: 40 });
      }
      h.push(
        { text: 'Name', value: 'PROVIDER_NAME', sortable: true },
        { text: 'NPI', value: 'PROVIDER_ID', sortable: true },
        { text: 'Specialty', value: 'PRIMARY_SPECIALTY', sortable: true },
        { text: 'Organization', value: 'ORGANIZATION', sortable: false },
        { text: 'Address', value: 'ADDRESS', sortable: false },
        { text: 'Phone', value: 'PHONE', sortable: false },
        { text: 'Details', value: 'DETAILS', sortable: false, width: 120, align: 'center' }
      );
      return h;
    },
    // Garante que cada item tenha a propriedade DETAILS para o slot do v-data-table
    providersWithDetails() {
      return (this.providers || []).map((p, i) => ({
        ...p,
        DETAILS: true,
        ROW_KEY:
          p.ROW_KEY ||
          `${p.PROVIDER_ID || 'row'}::${p.PRACTICE_ID != null && String(p.PRACTICE_ID).trim() !== '' ? p.PRACTICE_ID : `idx${i}`}`,
      }));
    },
    providerDetailsDialogKey() {
      const p = this.selectedProvider;
      if (!p) return '';
      if (p.MONGO_DOCUMENT_ID) return String(p.MONGO_DOCUMENT_ID);
      if (p.ROW_KEY) return p.ROW_KEY;
      return String(p.PROVIDER_ID || p.provider_id || p.NPI || '');
    },
  },
  mounted() {
    this.getUser();
    this.getInsurances();
    this.openDetailFromRoute();
  },
  methods: {
    openDetailFromRoute() {
      const npi = this.$route.params.npi;
      if (npi) {
        const fromList = (this.providers || []).find(p => (p.PROVIDER_ID || p.provider_id || p.NPI) === npi);
        this.selectedProvider = fromList || { PROVIDER_ID: npi, provider_id: npi, NPI: npi };
        this.detailsDialogOpen = true;
      }
    },
    closeDetailsDialog() {
      this.detailsDialogOpen = false;
      if (this.$route.params.npi) {
        this.$router.replace('/providers');
      }
    },
    onDetailsDialogChange(open) {
      if (!open && this.$route.params.npi) {
        this.$router.replace('/providers');
      }
    },
    async getUser() {
      try {
        const response = await axios.get('/api/user');
        const data = response.data;
        this.user = Array.isArray(data) ? data[0] : data;
        if (!this.user || typeof this.user !== 'object') {
          this.$router.push('/login');
          return;
        }
        this.checkUser();
      } catch (e) {
        this.$router.push('/login');
      }
    },
    async checkUser() {
      if (!this.user || typeof this.user !== 'object' || this.user.role == null) {
        return;
      }
      if (this.user.role === 'Clinical') {
        this.$router.push('/patients');
        return;
      }
      if (this.user.role !== 'Superuser' && this.user.role !== 'Admin') {
        await this.setPresets();
      }
      await this.loadSpecialtyOptions();
      this.getProviders();
    },
    async setPresets() {
      const role = this.user.role;
      if (this.user.insurance && this.user.insurance[0]) {
        this.selectedInsurance = this.user.insurance[0];
      }
      if (role === 'Group') {
        await axios.get(`api/list/npi/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data && response.data[0] ? response.data[0].BILLING_PROVIDER_NPI : [];
        });
      } else if (role === 'Affiliation') {
        await axios.get(`api/list/ipaId/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data || [];
        });
      }
    },
    async getInsurances() {
      await axios.get('api/list/insurances').then((response) => {
        this.insurances = response.data || [];
      });
    },
    /** Lista de specialties (API); com Primary Care usa mesma lista capitated do PCP. */
    async loadSpecialtyOptions() {
      try {
        const params = new URLSearchParams();
        if (this.providerTypeFilter === 'primarycare') {
          params.set('pcp', 'true');
        }
        const qs = params.toString();
        const url = qs ? `api/providers/specialties?${qs}` : 'api/providers/specialties';
        const response = await axios.get(url);
        const list = Array.isArray(response.data) ? response.data : [];
        this.specialtyOptions = list
          .filter((s) => s != null && String(s).trim() !== '')
          .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }));
      } catch (e) {
        this.specialtyOptions = [];
      }
    },
    buildGetAllParams() {
      const base = 'api/providers/getall/all';
      const term = this.searchTerm ? encodeURIComponent(this.searchTerm) : '';
      const path = term ? `${base}/${term}` : base;
      const params = new URLSearchParams();
      params.set('page', String(this.pagination.page));
      params.set('limit', String(this.pagination.limit));
      if (this.providerTypeFilter && this.providerTypeFilter !== 'all') params.set('providerType', this.providerTypeFilter);
      if (this.organizationFilter && this.organizationFilter.trim()) params.set('organization', this.organizationFilter.trim());
      if (this.specialtyFilter && this.specialtyFilter.trim()) params.set('specialty', this.specialtyFilter.trim());
      if (this.zipCodeFilter && this.zipCodeFilter.trim()) {
        params.set('zipcode', this.zipCodeFilter.trim());
        params.set(
          'radiusMiles',
          String(this.zipSearchRadiusMiles || 15)
        );
      }
      if (this.isAffiliation && this.affiliationOnlyFilter) params.set('affiliationOnly', 'true');
      return `${path}?${params.toString()}`;
    },
    onAffiliationFilterChange() {
      this.pagination.page = 1;
      this.getProviders();
    },
    getProviders: debounce(async function getProviders() {
      this.loading = true;
      const url = this.buildGetAllParams();
      try {
        const response = await axios.get(url);
        const res = response.data;
        if (res && typeof res === 'object' && 'data' in res && 'total' in res) {
          this.providers = Array.isArray(res.data) ? res.data : [];
          this.totalProviders = Number(res.total) || 0;
        } else {
          this.providers = Array.isArray(res) ? res : [];
          this.totalProviders = this.providers.length;
        }
      } catch (e) {
        this.errors.push(e);
        this.providers = [];
        this.totalProviders = 0;
      }
      this.loading = false;
    }, 1000),
    onPageChange(page) {
      this.pagination.page = page;
      this.getProviders();
    },
    onItemsPerPageChange(limit) {
      this.pagination.limit = limit;
      this.pagination.page = 1;
      this.getProviders();
    },
    openProviderDetails(item) {
      this.selectedProvider = item;
      this.detailsDialogOpen = true;
    },
    clearSearch() {
      this.searchTerm = '';
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
    getProviderStatus(provider) {
      if (new Date(provider.EFFECTIVE_END_DATE) > new Date()) {
        return 'success';
      } else if (provider.EFFECTIVE_END_DATE === null) {
        return 'warning';
      }
      return 'error';
    },
    getProviderStatusTooltip(provider) {
      if (new Date(provider.EFFECTIVE_END_DATE) > new Date()) {
        return 'Active';
      } else if (provider.EFFECTIVE_END_DATE === null) {
        return 'Unknown';
      }
      return 'Termed';
    },
    /** Ícone do MDI conforme PRIMARY_SPECIALTY; só ícones padrão MDI/Vuetify; fallback mdi-account */
    getSpecialtyIcon(provider) {
      const specialty = (provider && (provider.PRIMARY_SPECIALTY || '')).toLowerCase();
      if (!specialty) return 'mdi-account';
      const map = [
        { keywords: ['cardiology', 'cardiac', 'heart'], icon: 'mdi-heart' },
        { keywords: ['pediatric', 'pediatrics', 'child'], icon: 'mdi-baby-carriage' },
        { keywords: ['psychiatry', 'psychiatric', 'mental health', 'psychology', 'behavioral'], icon: 'mdi-brain' },
        { keywords: ['ophthalmology', 'optometry', 'eye', 'vision'], icon: 'mdi-eye' },
        { keywords: ['orthopedic', 'orthopaedic', 'sports medicine', 'bone'], icon: 'mdi-bone' },
        { keywords: ['dental', 'dentist', 'dentistry', 'tooth'], icon: 'mdi-tooth' },
        { keywords: ['dermatology', 'dermatologic', 'skin'], icon: 'mdi-medical-bag' },
        { keywords: ['obstetric', 'gynecology', 'ob/gyn', 'obgyn', 'women'], icon: 'mdi-baby-carriage' },
        { keywords: ['neurology', 'neurological'], icon: 'mdi-brain' },
        { keywords: ['radiology', 'imaging', 'x-ray'], icon: 'mdi-medical-bag' },
        { keywords: ['anesthesia', 'anesthesiology'], icon: 'mdi-needle' },
        { keywords: ['gastroenterology', 'gastrointestinal', 'nephrology', 'kidney', 'renal', 'pulmonology', 'pulmonary', 'respiratory', 'lung', 'oncology', 'cancer', 'urology', 'urological', 'ent', 'otolaryngology', 'ear', 'nose', 'throat', 'podiatry', 'podiatric', 'foot', 'surgery', 'surgical', 'endocrinology', 'diabetes', 'hormone', 'rheumatology', 'rheumat'], icon: 'mdi-medical-bag' },
        { keywords: ['nurse', 'np', 'nurse practitioner', 'pa ', 'physician assistant'], icon: 'mdi-medical-bag' },
        { keywords: ['pharmacy', 'pharmacist'], icon: 'mdi-pharmacy' },
        { keywords: ['physical therapy', 'physiotherapy', 'pt ', 'rehabilitation'], icon: 'mdi-dumbbell' },
        { keywords: ['internal medicine', 'family medicine', 'family practice', 'primary care', 'general practice', 'gp '], icon: 'mdi-stethoscope' },
        { keywords: ['pathology', 'pathologist'], icon: 'mdi-microscope' },
      ];
      for (const { keywords, icon } of map) {
        if (keywords.some(kw => specialty.includes(kw))) return icon;
      }
      return 'mdi-account';
    },
    organizationName(provider) {
      return (provider && (provider.ORGANIZATION_NAME || provider.GROUP_NAME)) || '';
    },
    fullAddress(provider) {
      if (!provider) return '';
      const parts = [
        provider.SERVICE_LOCATION_ADDRESS_1,
        provider.SERVICE_LOCATION_ADDRESS_2,
        [provider.SERVICE_LOCATION_CITY, provider.SERVICE_LOCATION_STATE].filter(Boolean).join(', '),
        provider.SERVICE_LOCATION_ZIP
      ].filter(Boolean);
      return parts.join(', ') || '';
    },
    phoneNumber(provider) {
      if (!provider) return '';
      return provider.SERVICE_LOCATION_PHONE || provider.BILLING_PHONE || '';
    },
  },
  watch: {
    '$route.params.npi': {
      handler() {
        this.openDetailFromRoute();
      },
    },
    searchTerm() {
      this.pagination.page = 1;
      this.getProviders();
    },
    providerTypeFilter() {
      this.pagination.page = 1;
      this.loadSpecialtyOptions();
      this.getProviders();
    },
    organizationFilter() {
      this.pagination.page = 1;
      this.getProviders();
    },
    specialtyFilter() {
      this.pagination.page = 1;
      this.getProviders();
    },
    zipCodeFilter() {
      this.pagination.page = 1;
      this.getProviders();
    },
    user: {
      handler(newVal, oldVal) {
        if (newVal == null && oldVal != null) {
          this.getUser();
        }
      },
    },
  },
};
</script>

<style scoped>
.providers-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}
.providers-table >>> .v-data-table td,
.providers-table >>> .v-data-table th {
  padding: 8px 16px !important;
}
</style>
